/**
 * Kleine, veilige markdown-renderer voor chat-antwoorden (geen externe dependency, geen ruwe HTML).
 * Alle tekst wordt eerst HTML-ge-escaped; alleen de markdown-constructies hieronder leveren tags op:
 * koppen, alinea's, vet/cursief/doorgehaald, code (inline + fenced), lijsten (genest), tabellen,
 * citaten, scheidingslijnen en links (http(s)/mailto of interne paden zoals /products/12).
 */
const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ESC[c]);
}

/** Alleen veilige doelen: http(s), mailto of een intern pad dat met één slash begint. */
export function safeHref(url: string): string | null {
  const u = url.trim();
  if (/^https?:\/\//i.test(u) || /^mailto:/i.test(u)) return u;
  if (/^\/(?!\/)/.test(u)) return u;
  return null;
}

function inlineNoCode(text: string): string {
  let s = escapeHtml(text);
  // URL mag gebalanceerde haakjes bevatten (bv. wikipedia-links); één niveau diep
  s = s.replace(/\[([^\]]+)\]\(((?:[^()\s]|\([^()\s]*\))+)\)/g, (_all, label: string, url: string) => {
    const href = safeHref(url);
    if (!href) return label;
    return href.startsWith('/')
      ? `<a href="${href}" data-internal="1">${label}</a>`
      : `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
  });
  s = s.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/__([^_\n]+)__/g, '<strong>$1</strong>');
  s = s.replace(/(^|[\s(>])\*([^*\n]+)\*(?=[\s.,;:!?)<]|$)/g, '$1<em>$2</em>');
  s = s.replace(/(^|[\s(>])_([^_\n]+)_(?=[\s.,;:!?)<]|$)/g, '$1<em>$2</em>');
  s = s.replace(/~~([^~\n]+)~~/g, '<del>$1</del>');
  return s;
}

/** Inline-opmaak; code-spans worden eerst apart gezet zodat er niets in vervangen wordt. */
export function renderInline(text: string): string {
  const parts: string[] = [];
  const re = /`([^`\n]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    parts.push(inlineNoCode(text.slice(last, m.index)));
    parts.push(`<code>${escapeHtml(m[1])}</code>`);
    last = m.index + m[0].length;
  }
  parts.push(inlineNoCode(text.slice(last)));
  return parts.join('');
}

const LIST_RE = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split(/(?<!\\)\|/).map((c) => c.replace(/\\\|/g, '|').trim());
}

/** Lijst vanaf regel `start` met inspringing `indent`; geeft de HTML en de eerstvolgende regelindex. */
function parseList(lines: string[], start: number, indent: number): { html: string; next: number } {
  const first = lines[start].match(LIST_RE)!;
  const ordered = /\d/.test(first[2]);
  const items: string[] = [];
  let i = start;
  while (i < lines.length) {
    const m = lines[i].match(LIST_RE);
    if (!m || m[1].length !== indent || /\d/.test(m[2]) !== ordered) break;
    let text = m[3];
    i++;
    const children: string[] = [];
    // vervolgregels (ingesprongen tekst) en geneste lijsten
    while (i < lines.length) {
      const line = lines[i];
      if (!line.trim()) break;
      const sub = line.match(LIST_RE);
      if (sub && sub[1].length > indent) {
        const nested = parseList(lines, i, sub[1].length);
        children.push(nested.html);
        i = nested.next;
        continue;
      }
      if (sub) break;
      const lead = line.match(/^(\s*)/)![1].length;
      if (lead <= indent) break;
      text += `\n${line.trim()}`;
      i++;
    }
    items.push(`<li>${renderInline(text).replace(/\n/g, '<br>')}${children.join('')}</li>`);
  }
  const tag = ordered ? 'ol' : 'ul';
  return { html: `<${tag}>${items.join('')}</${tag}>`, next: i };
}

export function renderMarkdown(src: string): string {
  const lines = (src ?? '').replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  const para: string[] = [];
  const flush = () => {
    if (!para.length) return;
    out.push(`<p>${renderInline(para.join('\n')).replace(/\n/g, '<br>')}</p>`);
    para.length = 0;
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const fence = line.match(/^\s*```(\w*)\s*$/);
    if (fence) {
      flush();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```\s*$/.test(lines[i])) buf.push(lines[i++]);
      i++;
      const cls = fence[1] ? ` class="lang-${escapeHtml(fence[1])}"` : '';
      out.push(`<pre><code${cls}>${escapeHtml(buf.join('\n'))}</code></pre>`);
      continue;
    }
    if (!line.trim()) {
      flush();
      i++;
      continue;
    }
    const h = line.match(/^(#{1,6})\s+(.*?)\s*#*\s*$/);
    if (h) {
      flush();
      const level = Math.min(6, h[1].length + 3); // # → h4 … (klein genoeg voor een chatpaneel)
      out.push(`<h${level} class="kl-md-h">${renderInline(h[2])}</h${level}>`);
      i++;
      continue;
    }
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flush();
      out.push('<hr>');
      i++;
      continue;
    }
    if (/^\s*>/.test(line)) {
      flush();
      const buf: string[] = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) buf.push(lines[i++].replace(/^\s*>\s?/, ''));
      out.push(`<blockquote>${renderMarkdown(buf.join('\n'))}</blockquote>`);
      continue;
    }
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEP_RE.test(lines[i + 1])) {
      flush();
      const head = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes('|')) rows.push(splitRow(lines[i++]));
      const th = head.map((c) => `<th>${renderInline(c)}</th>`).join('');
      const body = rows
        .map((r) => `<tr>${head.map((_, k) => `<td>${renderInline(r[k] ?? '')}</td>`).join('')}</tr>`)
        .join('');
      out.push(
        `<div class="kl-md-table"><table class="table table-sm table-bordered"><thead><tr>${th}</tr></thead><tbody>${body}</tbody></table></div>`,
      );
      continue;
    }
    const li = line.match(LIST_RE);
    if (li) {
      flush();
      const list = parseList(lines, i, li[1].length);
      out.push(list.html);
      i = list.next;
      continue;
    }
    para.push(line.trim());
    i++;
  }
  flush();
  return out.join('\n');
}
